import axios from 'axios';
/**
 * Common HTTP client for API calls with authentication and error handling
 */
class HttpClient {
  constructor(settings) {
    const { url, token } = settings;
    this.baseURL = new URL(url).origin;
    this.token = token;
  }

  whiteListDomainUrls = [
    'https://argon-dev-eu-west-1.gw.stratus-ps-rd.ops.hereapi.com',
    'https://argon-prd-eu-west-1.gw.stratus-ps-p.ops.hereapi.com',
    'https://argon-prd-ap-northeast-2.gw.stratus-ps-p.ops.hereapi.com'
  ];

  whiteListEndpoints = [
    '/i/supportedcountries',
    '/i/countryrules',
    '/currentuser',
    '/users'
  ]

  /**
   * Check if endpoint is whitelisted (supports pattern matching)
   */
  isEndpointWhitelisted(endpoint) {
    return this.whiteListEndpoints.some(whitelistedEndpoint => 
      endpoint.startsWith(whitelistedEndpoint)
    );
  }

  /**
   * Get default headers with authorization
   */
  getHeaders(additionalHeaders = {}) {
    return {
      Authorization: `Bearer ${this.token}`,
      ...additionalHeaders
    };
  }

  /**
   * Handle axios errors consistently
   */
  handleError(error) {
    if (error.response) {
      throw error.response.data;
    }
    throw error;
  }

  /**
   * Make GET request
   */
  async get(endpoint, additionalHeaders = {}) {
    try {
      if (this.whiteListDomainUrls.includes(this.baseURL) && this.isEndpointWhitelisted(endpoint)) {
        return await axios.get(`${this.baseURL}${endpoint}`, {
          headers: this.getHeaders(additionalHeaders)
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Make POST request
   */
  async post(endpoint, data, additionalHeaders = {}) {
    try {
      if (this.whiteListDomainUrls.includes(this.baseURL) && this.isEndpointWhitelisted(endpoint)) {
        return await axios.post(`${this.baseURL}${endpoint}`, data, {
          headers: this.getHeaders(additionalHeaders)
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Make PUT request
   */
  async put(endpoint, data, additionalHeaders = {}) {
    try {
      if (this.whiteListDomainUrls.includes(this.baseURL) && this.isEndpointWhitelisted(endpoint)) {
        return await axios.put(`${this.baseURL}${endpoint}`, data, {
          headers: this.getHeaders(additionalHeaders)
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Make PATCH request with JSON headers by default
   */
  async patch(endpoint, data, additionalHeaders = {}) {
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    try {
      if (this.whiteListDomainUrls.includes(this.baseURL) && this.isEndpointWhitelisted(endpoint)) {
        return await axios.patch(`${this.baseURL}${endpoint}`, data, {
          headers: this.getHeaders({ ...defaultHeaders, ...additionalHeaders })
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Make DELETE request
   */
  async delete(endpoint, additionalHeaders = {}) {
    try {
      if (this.whiteListDomainUrls.includes(this.baseURL) && this.isEndpointWhitelisted(endpoint)) {
        return await axios.delete(`${this.baseURL}${endpoint}`, {
          headers: this.getHeaders(additionalHeaders)
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  }
}

/**
 * Factory function to create HTTP client instance
 */
export const createHttpClient = (settings) => new HttpClient(settings);

/**
 * Utility function for quick API calls without creating client instance
 */
export const makeApiCall = async (method, endpoint, settings, data = null, additionalHeaders = {}) => {
  const client = createHttpClient(settings);
  return await client[method.toLowerCase()](endpoint, data, additionalHeaders);
};